import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import './EnergyFinancingTrends.scss';
import { API_BASE } from '../../config/environments/api';

const EnergyFinancingTrends = () => {
  const [activeFilter, setActiveFilter] = useState('Combined');
  const [chartData, setChartData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [years, setYears] = useState([]);

  const filters = ['Combined', 'World Bank', 'AfDB'];

  // Fetch financing trends data from API
  const fetchFinancingData = async (filter = 'Combined', isInitialLoad = false) => {
    try {
      // Only show loading on initial mount
      if (isInitialLoad) {
        setInitialLoading(true);
      }
      
      const url = `${API_BASE}/projects/financing-trends`;
      
      const requestBody = {
        reporting_org_filter: filter // 'Combined', 'AfDB', 'World Bank'
      };
      
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setChartData(result.data);
        setYears(result.data.map(item => item.year.toString()));
      } else {
        // Fallback to empty data
        setChartData([]);
        setYears([]);
      }
    } catch (error) {
      // Fallback to empty data
      setChartData([]);
      setYears([]);
    } finally {
      // Only set loading to false on initial load
      if (isInitialLoad) {
        setInitialLoading(false);
      }
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchFinancingData(activeFilter, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update data when filter changes (without showing loader)
  useEffect(() => {
    // Skip if this is the initial load (handled by first useEffect)
    if (initialLoading) {
      return;
    }
    fetchFinancingData(activeFilter, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  // Helper function to format values in Million/Billion
  const formatValue = (value) => {
    if (value >= 1000000000) {
      return (value / 1000000000).toFixed(2) + 'B';
    } else if (value >= 1000000) {
      return (value / 1000000).toFixed(2) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(2) + 'K';
    }
    return value.toFixed(2);
  };

  // Process data for chart - extract segment values
  // Use null instead of 0 for zero values so bars don't show
  const processedData = chartData.map(item => ({
    year: item.year,
    NA: item['NA'] && item['NA'] > 0 ? item['NA'] : null,
    FossilFuel: item['Fossil Fuel'] && item['Fossil Fuel'] > 0 ? item['Fossil Fuel'] : null,
    RenewableEnergy: item['Renewable Energy'] && item['Renewable Energy'] > 0 ? item['Renewable Energy'] : null,
    projectCount: item.projectCount && item.projectCount > 0 ? item.projectCount : null
  }));

  // Get current data based on active filter
  const currentData = {
    NA: processedData.map(item => item.NA),
    FossilFuel: processedData.map(item => item.FossilFuel),
    RenewableEnergy: processedData.map(item => item.RenewableEnergy),
    projectCount: processedData.map(item => item.projectCount)
  };

  // Calculate dataZoom range to show from 2010
  const getDataZoomRange = () => {
    if (years.length === 0) return [0, 100];

    const year2010Index = years.findIndex(year => parseInt(year) >= 2010);
    if (year2010Index === -1) {
      // If 2010 not found, show last 5 years
      const startIndex = Math.max(0, years.length - 5);
      const endIndex = years.length - 1;
      const startPercent = (startIndex / (years.length - 1)) * 100;
      const endPercent = (endIndex / (years.length - 1)) * 100;
      return [startPercent, endPercent];
    } else {
      // Show from 2010 to end
      const startPercent = (year2010Index / (years.length - 1)) * 100;
      return [startPercent, 100];
    }
  };

  // ECharts configuration with real data
  const chartOption = {
    animation: true,
    animationDuration: 500,
    animationEasing: 'cubicOut',
    title: {
      text: null,
      textStyle: {
        fontFamily: "'Poppins', sans-serif"
      }
    },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          textStyle: {
            fontFamily: "'Poppins', sans-serif",
            align: 'left'
          },
          formatter: function(params) {
            let tooltip = '<div style="padding: 5px; background: white; border-radius: 8px; font-family: \'Poppins\', sans-serif;">';
            tooltip += '<div style="font-size: 14px; font-weight: 500; color: #333; margin-bottom: 8px; font-family: \'Poppins\', sans-serif;">' + params[0].axisValue + '</div>';
            
            // Filter out items with zero or null values
            const validParams = params.filter(function(item) {
              return item.value !== null && item.value !== undefined && item.value !== 0;
            });
            
            // Calculate total for display (only from valid non-zero values)
            let totalCommitments = 0;
            validParams.forEach(function(item) {
              if (item.seriesName !== 'Project Count') {
                totalCommitments += item.value || 0;
              }
            });
            
            // Only show items with non-zero values in tooltip
            validParams.forEach(function(item) {
              if (item.seriesName === 'Project Count') {
                tooltip += '<div style="color: #666; margin-bottom: 4px; font-family: \'Poppins\', sans-serif;">' +
                          '<span style="color:' + item.color + '">●</span> ' + item.seriesName + ': <span style="font-weight: 600;color:#333">' + item.value + '</span>' +
                          '</div>';
              } else {
                const formattedValue = formatValue(item.value);
                tooltip += '<div style="color: #666; margin-bottom: 4px; font-family: \'Poppins\', sans-serif;">' +
                          '<span style="color:' + item.color + '">●</span> ' + item.seriesName + ': <span style="font-weight: 600;color:#333">' + '$' + formattedValue + ' USD</span>' +
                          '</div>';
              }
            });
            
            // Add total only if there are valid commitments
            if (totalCommitments > 0) {
              const formattedTotal = formatValue(totalCommitments);
              tooltip += '<div style="color: #333; border-top: 1px solid #eee; padding-top: 4px; margin-top: 4px; font-weight: 600; font-family: \'Poppins\', sans-serif;">Total: $' + formattedTotal + ' USD</div>';
            }
            
            tooltip += '<div style="color: #666; border-top: 1px solid #eee; padding-top: 4px; margin-top: 4px; font-family: \'Poppins\', sans-serif;">Source: <span style="font-weight: 600;color:#333">' + activeFilter + '</span></div>';
            tooltip += '</div>';
            return tooltip;
          }
        },
    legend: {
      data: ['Fossil Fuel', 'Renewable Energy', 'NA', 'Project Count'],
      top: 10,
      textStyle: {
        fontSize: 16,
        fontFamily: "'Poppins', sans-serif"
      }
    },
    grid: {
      left: '8%', // Increased to ensure yAxis label is always visible
      right: '8%', // Increased to ensure right yAxis label is always visible
      bottom: '15%', // Increased bottom margin for dataZoom
      top: '15%',
      containLabel: true
    },
    dataZoom: [
      {
        type: 'slider',
        show: true,
        xAxisIndex: [0],
        start: getDataZoomRange()[0],
        end: getDataZoomRange()[1],
        bottom: 15,
        height: 20,
        backgroundColor: '#f8f9fa',
        borderColor: '#e9ecef',
        fillerColor: 'rgba(135, 206, 250, 0.3)',
        selectedDataBackground: {
          areaStyle: {
            color: 'rgba(135, 206, 250, 0.2)'
          },
          lineStyle: {
            color: '#87ceeb',
            width: 1
          }
        },
        handleStyle: {
          color: '#87ceeb',
          borderColor: '#87ceeb',
          borderWidth: 1,
          shadowBlur: 2,
          shadowColor: 'rgba(0, 0, 0, 0.1)'
        },
        textStyle: {
          color: '#666',
          fontSize: 12,
          fontFamily: "'Poppins', sans-serif"
        },
        showDetail: false,
        showDataShadow: false,
        realtime: true,
        filterMode: 'filter'
      },
      {
        type: 'inside',
        xAxisIndex: [0],
        start: getDataZoomRange()[0],
        end: getDataZoomRange()[1],
        zoomLock: true,
        zoomOnMouseWheel: false,
        moveOnMouseMove: false,
        moveOnMouseWheel: false
      }
    ],
    xAxis: {
      type: 'category',
      data: years.length > 0 ? years : ['No Data'],
      axisLine: {
        lineStyle: {
          color: '#e5e5e5'
        }
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        color: '#666',
        fontSize: 12,
        fontFamily: "'Poppins', sans-serif"
      }
    },
        yAxis: [
          {
            type: 'value',
            name: 'Investment (USD)',
            nameLocation: 'middle',
            nameGap: 55,
            min: 0,
            axisLine: {
              lineStyle: {
                color: '#e5e5e5'
              }
            },
            axisTick: {
              show: false
            },
            axisLabel: {
              color: '#666',
              fontSize: 12,
              fontFamily: "'Poppins', sans-serif",
              show: true,
              formatter: function(value) {
                if (value >= 1000000000) {
                  return (value / 1000000000).toFixed(1) + 'B';
                } else if (value >= 1000000) {
                  return (value / 1000000).toFixed(1) + 'M';
                } else if (value >= 1000) {
                  return (value / 1000).toFixed(1) + 'K';
                }
                return value.toLocaleString();
              }
            },
            nameTextStyle: {
              color: '#666',
              fontSize: 12,
              fontFamily: "'Poppins', sans-serif",
              padding: [0, 0, 0, 0]
            },
            splitLine: {
              lineStyle: {
                color: '#f0f0f0'
              }
            }
          },
      {
        type: 'value',
        name: 'Project Count',
        nameLocation: 'middle',
        nameGap: 50,
        min: 0,
        position: 'right',
        axisLine: {
          lineStyle: {
            color: '#e5e5e5'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 12,
          fontFamily: "'Poppins', sans-serif",
          show: true
        },
        nameTextStyle: {
          color: '#666',
          fontSize: 12,
          fontFamily: "'Poppins', sans-serif",
          padding: [0, 0, 0, 0]
        },
        splitLine: {
          show: false
        }
      }
    ],
    series: [
      {
        name: 'Fossil Fuel',
        type: 'bar',
        stack: 'commitments',
        yAxisIndex: 0,
        data: currentData.FossilFuel,
        itemStyle: {
          color: '#000'
        },
        barWidth: 30
      },
      {
        name: 'Renewable Energy',
        type: 'bar',
        stack: 'commitments',
        yAxisIndex: 0,
        data: currentData.RenewableEnergy,
        itemStyle: {
          color: '#D48019'
        },
        barWidth: 30
      },
      {
        name: 'NA',
        type: 'bar',
        stack: 'commitments',
        yAxisIndex: 0,
        data: currentData.NA,
        itemStyle: {
          color: '#E6A047'
        },
        barWidth: 30
      },
      {
        name: 'Project Count',
        type: 'line',
        yAxisIndex: 1,
        data: currentData.projectCount,
        itemStyle: {
          color: '#FF6600'
        },
        lineStyle: {
          color: '#FF6600',
          width: 3
        },
        symbol: 'circle',
        symbolSize: 6
      },
    ],
      // Media queries for responsive design
      media: [
        {
          // Tablet and below (max-width: 1024px)
          query: { maxWidth: 1024 },
          option: {
            dataZoom: [
              {
                type: 'slider',
                show: true,
                xAxisIndex: [0],
                // Show only 4 bars at a time on tablet (last 4 bars)
                start: years.length > 4 ? Math.max(0, ((years.length - 4) / Math.max(1, years.length - 1)) * 100) : 0,
                end: 100,
                bottom: 20,
                height: 25,
                backgroundColor: '#f8f9fa',
                borderColor: '#e9ecef',
                fillerColor: 'rgba(135, 206, 250, 0.3)',
                selectedDataBackground: {
                  areaStyle: {
                    color: 'rgba(135, 206, 250, 0.2)'
                  },
                  lineStyle: {
                    color: '#87ceeb',
                    width: 1
                  }
                },
                handleStyle: {
                  color: '#87ceeb',
                  borderColor: '#87ceeb',
                  borderWidth: 1,
                  shadowBlur: 2,
                  shadowColor: 'rgba(0, 0, 0, 0.1)'
                },
                textStyle: {
                  color: '#666',
                  fontSize: 10,
                  fontFamily: "'Poppins', sans-serif"
                },
                showDetail: false,
                showDataShadow: false,
                realtime: true,
                filterMode: 'filter'
              },
              {
                type: 'inside',
                xAxisIndex: [0],
                start: years.length > 4 ? Math.max(0, ((years.length - 4) / Math.max(1, years.length - 1)) * 100) : 0,
                end: 100,
                zoomLock: true,
                zoomOnMouseWheel: false,
                moveOnMouseMove: false,
                moveOnMouseWheel: false
              }
            ]
          }
        },
        {
          // Mobile (max-width: 768px)
          query: { maxWidth: 768 },
          option: {
            dataZoom: [
              {
                type: 'slider',
                show: true,
                xAxisIndex: [0],
                // Show only 3 bars at a time on mobile (last 3 bars)
                start: years.length > 4 ? Math.max(0, ((years.length - 4) / Math.max(1, years.length - 1)) * 100) : 0,
                end: 100,
                bottom: 25,
                height: 30,
                backgroundColor: '#f8f9fa',
                borderColor: '#e9ecef',
                fillerColor: 'rgba(135, 206, 250, 0.3)',
                selectedDataBackground: {
                  areaStyle: {
                    color: 'rgba(135, 206, 250, 0.2)'
                  },
                  lineStyle: {
                    color: '#87ceeb',
                    width: 1
                  }
                },
                handleStyle: {
                  color: '#87ceeb',
                  borderColor: '#87ceeb',
                  borderWidth: 1,
                  shadowBlur: 2,
                  shadowColor: 'rgba(0, 0, 0, 0.1)'
                },
                textStyle: {
                  color: '#666',
                  fontSize: 9,
                  fontFamily: "'Poppins', sans-serif"
                },
                showDetail: false,
                showDataShadow: false,
                realtime: true,
                filterMode: 'filter'
              },
              {
                type: 'inside',
                xAxisIndex: [0],
                start: years.length > 3 ? Math.max(0, ((years.length - 3) / Math.max(1, years.length - 1)) * 100) : 0,
                end: 100,
                zoomLock: true,
                zoomOnMouseWheel: false,
                moveOnMouseMove: false,
                moveOnMouseWheel: false
              }
            ]
          }
        }
      ]
  };

  // Highcharts Column Chart with overlapping columns and multiple y-axes
  // const chartOptions = {
  //   chart: {
  //     type: 'column',
  //     marginTop: 50,
  //     spacingRight: 30,
  //     backgroundColor: 'transparent',
  //     height: 400
  //   },
  //   title: {
  //     text: null
  //   },
  //   xAxis: {
  //     categories: ['2017', '2018', '2019'],
  //     lineColor: '#e5e5e5',
  //     tickColor: 'transparent',
  //     labels: {
  //       style: {
  //         color: '#666',
  //         fontSize: '12px',
  //         fontFamily: 'Spartan, sans-serif'
  //       }
  //     }
  //   },
  //   yAxis: [
  //     {
  //       // Left Y-axis for Energy Access (Billions USD)
  //       title: {
  //         text: 'Energy Access (Billions USD)',
  //         style: {
  //           color: '#8b6fc4',
  //           fontSize: '12px',
  //           fontFamily: 'Spartan, sans-serif'
  //         }
  //       },
  //       min: 0,
  //       max: 4,
  //       tickInterval: 1,
  //       lineColor: '#e5e5e5',
  //       gridLineColor: '#f0f0f0',
  //       labels: {
  //         style: {
  //           color: '#8b6fc4',
  //           fontSize: '12px',
  //           fontFamily: 'Spartan, sans-serif'
  //         }
  //       }
  //     },
  //     {
  //       // Right Y-axis for Number of Projects
  //       title: {
  //         text: 'Number of Projects',
  //         style: {
  //           color: '#d9534f',
  //           fontSize: '12px',
  //           fontFamily: 'Spartan, sans-serif'
  //         }
  //       },
  //       min: 0,
  //       max: 200,
  //       tickInterval: 50,
  //       opposite: true,
  //       lineColor: '#e5e5e5',
  //       gridLineColor: 'transparent',
  //       labels: {
  //         style: {
  //           color: '#d9534f',
  //           fontSize: '12px',
  //           fontFamily: 'Spartan, sans-serif'
  //         }
  //       }
  //     },
  //     {
  //       // Third Y-axis for Renewable Energy %
  //       title: {
  //         text: 'Renewable Energy (%)',
  //         style: {
  //           color: '#5cb85c',
  //           fontSize: '12px',
  //           fontFamily: 'Spartan, sans-serif'
  //         }
  //       },
  //       min: 0,
  //       max: 60,
  //       tickInterval: 10,
  //       opposite: false,
  //       offset: 60,
  //       lineColor: '#e5e5e5',
  //       gridLineColor: 'transparent',
  //       labels: {
  //         style: {
  //           color: '#5cb85c',
  //           fontSize: '12px',
  //           fontFamily: 'Spartan, sans-serif'
  //         }
  //       }
  //     },
  //     {
  //       // Fourth Y-axis for CO2 Reduction
  //       title: {
  //         text: 'CO2 Reduction (Million tons)',
  //         style: {
  //           color: '#f0ad4e',
  //           fontSize: '12px',
  //           fontFamily: 'Spartan, sans-serif'
  //         }
  //       },
  //       min: 0,
  //       max: 4,
  //       tickInterval: 1,
  //       opposite: true,
  //       offset: -60,
  //       lineColor: '#e5e5e5',
  //       gridLineColor: 'transparent',
  //       labels: {
  //         style: {
  //           color: '#f0ad4e',
  //           fontSize: '12px',
  //           fontFamily: 'Spartan, sans-serif'
  //         }
  //       }
  //     }
  //   ],
  //   tooltip: {
  //     shared: true,
  //     useHTML: true,
  //     backgroundColor: 'rgba(255, 255, 255, 0.95)',
  //     borderColor: '#ddd',
  //     borderRadius: 8,
  //     shadow: true,
  //     formatter: function() {
  //       let tooltip = '<b>' + this.x + '</b><br/>';
  //       this.points.forEach(function(point) {
  //         if (point.series.name === 'Energy Access') {
  //           tooltip += '<span style="color:' + point.color + '">●</span> ' + 
  //                     point.series.name + ': $' + point.y + 'B<br/>';
  //         } else if (point.series.name === 'Number of Projects') {
  //           tooltip += '<span style="color:' + point.color + '">●</span> ' + 
  //                     point.series.name + ': ' + point.y + '<br/>';
  //         } else if (point.series.name === 'Renewable Energy') {
  //           tooltip += '<span style="color:' + point.color + '">●</span> ' + 
  //                     point.series.name + ': ' + point.y + '%<br/>';
  //         } else if (point.series.name === 'CO2 Reduction') {
  //           tooltip += '<span style="color:' + point.color + '">●</span> ' + 
  //                     point.series.name + ': ' + point.y + 'M tons<br/>';
  //         }
  //       });
  //       tooltip += '<b>Source: ' + activeFilter + '</b>';
  //       return tooltip;
  //     }
  //   },
  //   plotOptions: {
  //     column: {
  //       borderRadius: 4,
  //       borderWidth: 0,
  //       pointPadding: 0.1,
  //       groupPadding: 0.1,
  //       dataLabels: {
  //         enabled: false
  //       }
  //     }
  //   },
  //   series: [
  //     {
  //       name: 'Energy Access',
  //       type: 'column',
  //       yAxis: 0,
  //       data: currentData.series1,
  //       color: '#8b6fc4', // Purple
  //       pointPlacement: 0.15, // Slight offset for overlapping
  //       zIndex: 4
  //     },
  //     {
  //       name: 'Number of Projects',
  //       type: 'column',
  //       yAxis: 1,
  //       data: currentData.series2,
  //       color: '#d9534f', // Red
  //       pointPlacement: 0.05, // Slight offset for overlapping
  //       zIndex: 3
  //     },
  //     {
  //       name: 'Renewable Energy',
  //       type: 'column',
  //       yAxis: 2,
  //       data: currentData.series3,
  //       color: '#5cb85c', // Green
  //       pointPlacement: -0.05, // Slight offset for overlapping
  //       zIndex: 2
  //     },
  //     {
  //       name: 'CO2 Reduction',
  //       type: 'column',
  //       yAxis: 3,
  //       data: currentData.series4,
  //       color: '#f0ad4e', // Orange
  //       pointPlacement: -0.15, // Slight offset for overlapping
  //       zIndex: 1
  //     }
  //   ],
  //   credits: {
  //     enabled: false
  //   },
  //   legend: {
  //     enabled: true,
  //     align: 'center',
  //     verticalAlign: 'bottom',
  //     layout: 'horizontal',
  //     itemStyle: {
  //       fontSize: '12px',
  //       color: '#333',
  //       fontFamily: 'Spartan, sans-serif'
  //     },
  //     symbolHeight: 12,
  //     symbolWidth: 12,
  //     symbolRadius: 6,
  //     margin: 20,
  //     itemMarginTop: 5,
  //     itemMarginBottom: 5
  //   }
  // };

  // const chartOptions = {
  //   chart: {
  //     type: 'column',
  //     marginTop: 50,
  //     spacingRight: 30,
  //     backgroundColor: 'transparent',
  //     height: 400
  //   },
  //   title: {
  //     text: null
  //   },
  //   xAxis: {
  //     categories: ['2017', '2018', '2019'],
  //     lineColor: '#e5e5e5',
  //     tickColor: 'transparent',
  //     labels: {
  //       style: {
  //         color: '#666',
  //         fontSize: '12px',
  //         fontFamily: 'Spartan, sans-serif'
  //       }
  //     }
  //   },
  //   yAxis: {
  //     min: 0,
  //     max: 4,
  //     tickInterval: 1,
  //     lineColor: '#e5e5e5',
  //     gridLineColor: '#f0f0f0',
  //     labels: {
  //       style: {
  //         color: '#666',
  //         fontSize: '12px',
  //         fontFamily: 'Spartan, sans-serif'
  //       }
  //     }
  //   },
  //   tooltip: {
  //     shared: true,
  //     useHTML: true,
  //     backgroundColor: 'rgba(255, 255, 255, 0.95)',
  //     borderColor: '#ddd',
  //     borderRadius: 8,
  //     shadow: true,
  //     formatter: function() {
  //       let tooltip = '<b>' + this.x + '</b><br/>';
  //       this.points.forEach(function(point) {
  //         tooltip += '<span style="color:' + point.color + '">●</span> ' + 
  //                   point.series.name + ': ' + point.y + '<br/>';
  //       });
  //       tooltip += '<b>Source: ' + activeFilter + '</b>';
  //       return tooltip;
  //     }
  //   },
  //   plotOptions: {
  //     column: {
  //       borderRadius: 4,
  //       borderWidth: 0,
  //       pointPadding: 0.1,
  //       groupPadding: 0.1,
  //       dataLabels: {
  //         enabled: false
  //       },
  //       grouping: true
  //     }
  //   },
  //   series: [
  //     {
  //       name: 'Energy Access',
  //       data: currentData.series1,
  //       color: 'rgba(139, 111, 196, 0.6)', // Transparent purple
  //       pointPadding: 0.1
  //     },
  //     {
  //       name: 'Renewable Energy',
  //       data: currentData.series2,
  //       color: 'rgba(249, 115, 22, 0.6)', // Transparent orange
  //       pointPadding: 0.1
  //     }
  //   ],
  //   credits: {
  //     enabled: false
  //   },
  //   legend: {
  //     enabled: false
  //   }
  // };

  return (
    <section className="energy-financing-section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Energy Financing <span style={{ color: '#d17c1b'}}>Trends</span></h2>
          <p className="section-subtitle">
            Year-on-year financing data for energy access and renewable energy projects.
          </p>
        </div>

        <div className="chart-container">
          <div className="chart-filters">
            {filters.map(filter => (
              <button
                key={filter}
                className={`filter-btn ${activeFilter === filter ? 'active' : ''}`}
                onClick={() => {
                  setActiveFilter(filter);
                }}
              >
                {filter}
              </button>
            ))}
          </div>

          <div className="chart-content">
            {initialLoading ? (
              <div className="loading-container">
                <img 
                  src="/spinner/oval-spinner.gif" 
                  alt="Loading..." 
                  className="chart-loader-spinner"
                />
              </div>
            ) : (
              <div className="bar-chart-wrapper">
                <ReactECharts
                  option={chartOption}
                  style={{ height: '480px', width: '100%' }}
                  opts={{ renderer: 'canvas' }}
                  notMerge={false}
                  lazyUpdate={false}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EnergyFinancingTrends;


